import { useContext, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ToastContent, ToastTiming, toast } from "@stubhub/broadway-web";
import { useFeatureParticipationRecorder, useFeatureTernary } from "@viagogo/feature";
import type { IEventGridItem, PaginatedQueryOfIEventGridItem } from "src/http/types";
import { useContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { FavoritesContext } from "src/website-apps/shared/components/grid/favorites-fs/FavoritesContextTyped";
import type { FavoriteEventSummary } from "src/website-apps/shared/components/grid/favorites-fs/types";

const SAVED_EVENTS_HOME_QUERY_KEY = createQueryKey(QueryKeyPrefix.SAVED_EVENTS_HOME, {
  subscriptionType: ENTITY_TYPES.EVENT,
});

const SAVED_TOAST_CONTENT_ID = "cid$#4430672#"; // "Saved to your favorites"

// Only the fields a saved-event card renders are set. Price, availability, and other server-computed fields
// are intentionally omitted; the settle invalidation refetches the event and fills them in.
const toGridItem = (summary: FavoriteEventSummary): IEventGridItem => ({
  eventId: summary.eventId,
  eventName: summary.eventName ?? null,
  localEventDate: summary.localEventDate ?? null,
  localEventTime: summary.localEventTime ?? null,
  dayOfWeek: summary.dayOfWeek ?? null,
  imageUrl: summary.imageUrl ?? null,
  url: summary.url ?? null,
  venueName: summary.venueName ?? null,
  isFavorite: true,
});

/**
 * Keeps the home "Your saved events" carousel in step with favorite toggles, behind experiment
 * TM-Home-SavedEventsFavoriteSync (104492).
 *
 * On a toggle in the variant the event joins or leaves the saved-events query cache right away, so the carousel
 * shows the change without a reload, and a toast confirms a save. When the server call settles the query is
 * invalidated, so the list reconciles with the server. Control keeps the snapshot behavior.
 *
 * Only a logged-in buyer who toggles a favorite can observe the change, so participation is recorded on that
 * toggle (both arms) rather than on render.
 */
export const useSavedEventsFavoritesSync = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const { subscribeToFavoriteChange } = useContext(FavoritesContext);
  const { isUserLoggedIn } = useAppContext();

  const savedEventsFeature = useFeatureTernary(FeatureSetEnum.TMHomeSavedEventsFavoriteSync, () => isUserLoggedIn);
  const isVariant = savedEventsFeature({ control: () => false, variant: () => true, record: false });
  const recordParticipation = useFeatureParticipationRecorder(
    FeatureSetEnum.TMHomeSavedEventsFavoriteSync,
    () => isUserLoggedIn,
  );
  const savedToastMessage = useContent(SAVED_TOAST_CONTENT_ID);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    return subscribeToFavoriteChange((change) => {
      if (change.entityType !== ENTITY_TYPES.EVENT) {
        return;
      }

      if (change.status === "optimistic") {
        if (change.isFavorite) {
          recordParticipation();
        }
        if (!isVariant) {
          return;
        }
        if (change.isFavorite && savedToastMessage) {
          toast({ children: <ToastContent>{savedToastMessage}</ToastContent>, timing: ToastTiming.Short });
        }
        queryClient.setQueryData<PaginatedQueryOfIEventGridItem>(SAVED_EVENTS_HOME_QUERY_KEY, (prev) => {
          // Only edit the cache when the query is already populated for this page; never create an entry.
          if (!prev) {
            return prev;
          }
          const items = prev.items ?? [];
          const alreadyPresent = items.some((item) => item.eventId === change.entityId);

          if (change.isFavorite) {
            if (alreadyPresent || !change.eventSummary) {
              return prev;
            }
            return {
              ...prev,
              items: [toGridItem(change.eventSummary), ...items],
              totalCount: (prev.totalCount ?? items.length) + 1,
            };
          }

          if (!alreadyPresent) {
            return prev;
          }
          return {
            ...prev,
            items: items.filter((item) => item.eventId !== change.entityId),
            totalCount: Math.max(0, (prev.totalCount ?? items.length) - 1),
          };
        });
        return;
      }

      if (!isVariant) {
        return;
      }
      queryClient.invalidateQueries({ queryKey: SAVED_EVENTS_HOME_QUERY_KEY }).catch(() => {});
    });
  }, [enabled, isVariant, recordParticipation, savedToastMessage, queryClient, subscribeToFavoriteChange]);
};
